import {useCallback, useState} from "react";
import {useAppStore} from "../../../StateManager";
import {ButtonV5} from "../button/ButtonV5";
import {AddUserOrUserGroupV5} from "../details/AddUserOrUserGroupV5";
import {baseDistance, Flex1, FlexCenter} from "../styles/styles";
import type {ViewSharingSettingsData} from "../../../generated/api/base";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import type {User} from "../../../data/models/User";
import type {UserGroup} from "../../../data/models/UserGroup";
import type {ReportSharingSettingsData} from "../../../generated/api/reports";
import {ConfirmWindowV5} from "../popup/ConfirmWindowV5";
import {notify} from "../../../utils/Notify";
import {NotificationType} from "../../notification/Notification";
import {ShareOption} from "../../modules/abstract/view/sharing/ShareOption";
import {GroupShareSelectorV5} from "./GroupShareSelectorV5";
import type {SelectedItem} from "./SharingUtils";
import {RowStyled, SectionTitle, SharingPanelStyled, SharingStyled} from "./SharingPanel.styled";
import {renderItemWithInitials} from "./ItemWithInitials";

export interface IGlobalViewFolderSharingSettingsData {
	userID?: string | null;
	userGroupID?: string | null;
}

interface IEditViewSharingProps {
	readonly onAddClick: (id: string, permission: Permission.View | Permission.Update, feature: XyiconFeature) => void;
	readonly onDeleteUserGroup: (userGroup: UserGroup) => Promise<any>;
	readonly onDeleteUser: (user: User) => Promise<any>;
	readonly onConfirmOwnershipChange: (sharing: ViewSharingSettingsData | ReportSharingSettingsData | IGlobalViewFolderSharingSettingsData) => void;
	readonly onSaveChanges: () => Promise<any>;
	readonly sharingSettings: ViewSharingSettingsData[] | ReportSharingSettingsData[] | IGlobalViewFolderSharingSettingsData[];
	readonly ownedByUserId: string;
	readonly itemLabel: "View" | "Report" | "View Folder" | "Global View" | "Global Report";
	readonly itemName: string;
	readonly showShareOption: boolean;
	readonly isGlobal?: boolean;
	readonly hiddenUserGroupIds?: string[];
}

const SharingContentCommon = (props: IEditViewSharingProps) => {
	const appState = useAppStore((state) => state.appState);
	const {
		onAddClick,
		onDeleteUserGroup,
		onDeleteUser,
		onConfirmOwnershipChange,
		onSaveChanges,
		sharingSettings,
		ownedByUserId,
		itemLabel,
		itemName,
		showShareOption,
		isGlobal,
		hiddenUserGroupIds = [],
	} = props;

	const [isDeletePopupWindowOpen, setIsDeletePopupWindowOpen] = useState<boolean>(false);
	const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
	const [permissionType, setPermissionType] = useState<Permission.View | Permission.Update>(Permission.View);
	const [isSharing, setIsSharing] = useState<boolean>(false);

	const allUsers = appState.actions.getList<User>(XyiconFeature.User);
	const userGroups = appState.actions.getList<UserGroup>(XyiconFeature.UserGroup).filter((userGroup) => !hiddenUserGroupIds.includes(userGroup.id));
	const sharing = sharingSettings || [];
	const userGroupSharingList = sharing.filter((sh) => sh.userGroupID);
	const userSharingList = sharing.filter((sh) => sh.userID);
	const itemOwnerMaybe: User | null = isGlobal ? null : allUsers.find((user) => user.id === ownedByUserId);
	const users = allUsers.filter((user) => user.id !== ownedByUserId);
	const exceptionIds: string[] = [...userGroupSharingList.map((sh) => sh.userGroupID), ...userSharingList.map((sh) => sh.userID)];
	const exceptionsWithCurrentUser = [...exceptionIds, !isGlobal && appState.user?.id];
	const isReport = itemLabel.includes("Report");

	let sharedUsers: User[] = [];
	let sharedUserGroups: UserGroup[] = [];

	if (exceptionsWithCurrentUser?.length) {
		sharedUsers = users.filter((user) => exceptionsWithCurrentUser.includes(user.id));
	}

	if (exceptionIds?.length) {
		sharedUserGroups = userGroups.filter((userGroup) => exceptionIds.includes(userGroup.id));
	}

	const isUserOrUserGroupAdmin = useCallback(
		(selectedUser: SelectedItem) => {
			const {feature, id, itemName} = selectedUser;
			const getUserById = appState.actions.getUserById;

			const isAdminUserGroup = feature === XyiconFeature.UserGroup && itemName === "Administrators";
			const isCustomUserGroup = feature === XyiconFeature.UserGroup;
			const isAdmin = getUserById(id)?.isAdmin;

			if (isAdminUserGroup) {
				return true;
			}

			if (isCustomUserGroup) {
				const userGroup = userGroups.find((group) => group.id === id);
				const userGroupUsers = userGroup.userIds;
				const isAllAdmins = userGroupUsers.every((userId) => getUserById(userId)?.isAdmin);

				return isAllAdmins;
			}

			if (isAdmin) {
				return true;
			}

			return false;
		},
		[appState.actions.getUserById, userGroups],
	);

	const hasEdit = useCallback(
		(userOrUserGroup?: User | UserGroup) => {
			if (!isReport) {
				return true;
			}

			/* When sharing reports, if the selected user is non-admin or selected user group contains non-admin users,
			we should not show the "Edit" option. */
			if (userOrUserGroup) {
				const {ownFeature, id} = userOrUserGroup;
				const name = ownFeature === XyiconFeature.UserGroup ? userOrUserGroup.name : "";
				const isAdmin = isUserOrUserGroupAdmin({feature: ownFeature, id: id, itemName: name});

				return isAdmin;
			}

			const isAllAdmin = selectedItems.every((user) => isUserOrUserGroupAdmin(user));

			return isAllAdmin;
		},
		[isReport, selectedItems, isUserOrUserGroupAdmin],
	);

	const handleClick = (event: React.MouseEvent) => {
		event?.stopPropagation();
	};

	const onDeleteItem = async (item: User | UserGroup) => {
		if (!isDeletePopupWindowOpen) {
			setIsDeletePopupWindowOpen(true);
			const confirmed = await ConfirmWindowV5.open("Are you sure you want to Unshare selected 1 item?", "Confirm Unshare", {ok: "Unshare"});
			setIsDeletePopupWindowOpen(false);

			if (confirmed) {
				if (item.ownFeature === XyiconFeature.User) {
					onDeleteUser(item);
				} else {
					onDeleteUserGroup(item);
				}
			}
		}
	};

	const onShareClick = async () => {
		setIsSharing(true);
		selectedItems.forEach((item) => {
			onAddClick(item.id, permissionType, item.feature);
		});

		const result = await onSaveChanges();

		if (result) {
			const title = (
				<>
					{itemName} {itemLabel.toLowerCase()} has been shared with {getSharedWithText(selectedItems)} successfully!
				</>
			);

			notify(appState.app.notificationContainer, {
				type: NotificationType.Message,
				title,
				lifeTime: 10000,
			});
		}

		setSelectedItems([]);
		setIsSharing(false);
	};

	const getSharedWithText = (items: SelectedItem[]) => {
		if (items.length === 1) {
			const item = items[0];

			if (item.feature === XyiconFeature.User) {
				return appState.actions.getFeatureItemById<User>(item.id, XyiconFeature.User)?.fullName ?? item.itemName;
			}

			return item.itemName;
		}

		const userCount = items.filter((item) => item.feature === XyiconFeature.User).length;
		const userGroupCount = items.length - userCount;
		const parts: string[] = [];

		if (userCount > 0) {
			parts.push(`${userCount} user${userCount > 1 ? "s" : ""}`);
		}

		if (userGroupCount > 0) {
			parts.push(`${userGroupCount} user group${userGroupCount > 1 ? "s" : ""}`);
		}

		return parts.join(" and ");
	};

	const setPermission = (sharing: ViewSharingSettingsData | ReportSharingSettingsData, permission: boolean) => {
		if (itemLabel.includes("View")) {
			(sharing as ViewSharingSettingsData).canEditSharedView = permission;
		} else if (itemLabel.includes("Report")) {
			(sharing as ReportSharingSettingsData).canEditSharedReport = permission;
		}
	};

	const setUserPermission = async (permission: number, sharing: ViewSharingSettingsData | ReportSharingSettingsData) => {
		const canEdit = permission === Permission.Update;

		if (permission === ShareOption.OWNER) {
			if (!isDeletePopupWindowOpen) {
				setIsDeletePopupWindowOpen(true);
				const confirmed = await ConfirmWindowV5.open(
					`You will be removed as the owner of the "${itemName}" ${itemLabel} but will retain edit privileges. Do you want to continue?`,
					"Confirm Ownership Change",
					{ok: "Change Owner", cancel: "Cancel"},
				);
				setIsDeletePopupWindowOpen(false);

				if (confirmed) {
					onConfirmOwnershipChange(sharing);
				}
			}
		} else {
			setPermission(sharing, canEdit);
		}
		onSaveChanges();
	};

	const setUserGroupPermission = (permission: number, sharing: ViewSharingSettingsData | ReportSharingSettingsData) => {
		const canEdit = permission === Permission.Update;

		setPermission(sharing, canEdit);
		onSaveChanges();
	};

	return (
		<SharingStyled onClick={handleClick}>
			<FlexCenter $gap={baseDistance.sm}>
				<AddUserOrUserGroupV5
					exceptions={exceptionsWithCurrentUser}
					selectedItemList={selectedItems}
					ownedByUserId={ownedByUserId}
					setSelectedItems={(items) => setSelectedItems(items)}
					onDeleteItem={onDeleteItem}
					setPermissionType={(value) => setPermissionType(value)}
					isGlobal={isGlobal}
					isDropdown={!isGlobal}
					disabled={itemLabel === "View Folder"}
					hiddenUserGroupIds={hiddenUserGroupIds}
					hasEdit={hasEdit()}
				/>
				<ButtonV5
					label={isSharing ? "Sharing..." : "Share"}
					title="Share"
					className="primary rounded dark"
					disabled={selectedItems.length === 0 || isSharing}
					onClick={onShareClick}
				/>
			</FlexCenter>
			<SharingPanelStyled>
				<div className="borderBottom">
					<SectionTitle>User Groups</SectionTitle>
					<GroupShareSelectorV5
						items={sharedUserGroups}
						addedItems={userGroups}
						sharingList={userGroupSharingList}
						isSharing={true}
						showShareOption={showShareOption}
						onDeleteItem={onDeleteItem}
						setUserGroupPermission={setUserGroupPermission}
						isGlobal={isGlobal}
						itemLabel={itemLabel}
						feature={XyiconFeature.UserGroup}
						hasEdit={hasEdit}
					/>
				</div>
				<div>
					<SectionTitle>Users</SectionTitle>
					{itemOwnerMaybe && (
						<RowStyled>
							{renderItemWithInitials(itemOwnerMaybe, "big")}
							<Flex1 />
							<div className="sharedStyles">Owner</div>
						</RowStyled>
					)}
					<GroupShareSelectorV5
						items={sharedUsers}
						addedItems={users}
						sharingList={userSharingList}
						isSharing={true}
						showShareOption={showShareOption}
						onDeleteItem={onDeleteItem}
						setUserPermission={setUserPermission}
						feature={XyiconFeature.User}
						itemLabel={itemLabel}
						hasEdit={hasEdit}
					/>
				</div>
			</SharingPanelStyled>
		</SharingStyled>
	);
};

export default SharingContentCommon;
